import type { ChatMessageType } from "@/api/chat";

/**
 * Merge server-fetched chat history with live WebSocket messages into the list
 * to render. Pure so the ordering/dedup edge cases are unit-testable.
 *
 * History is the chronological past (the API returns it in order); live messages
 * arrive afterwards in WebSocket (send) order. So the correct order is simply
 * history-then-live — which also fixes the old bug where a live message that
 * arrived *while history was still loading* got prepended and jumped ahead of
 * the history that loaded a moment later.
 *
 * Live messages already present in history (the fetch window overlapped a live
 * arrival, or a refetch re-returned an echoed message) are dropped by id so they
 * don't render twice.
 */
export function mergeChatMessages(
  history: ChatMessageType[],
  live: ChatMessageType[],
): ChatMessageType[] {
  const historyIds = new Set(
    history.filter((m) => m.id).map((m) => m.id),
  );
  const liveNew = live.filter((m) => !(m.id && historyIds.has(m.id)));
  return [...history, ...liveNew];
}

/** True only for a WebSocket MessageEvent that has not already been consumed. */
export function isUnseenSocketMessage(
  message: MessageEvent | null,
  lastHandled: MessageEvent | null,
): boolean {
  return message !== null && message !== lastHandled;
}
